export const WRONG_PORTAL_LOGOUT_REASON = 'wrong-portal';

export type AuthLogoutReason = 'session-expired' | typeof WRONG_PORTAL_LOGOUT_REASON;

const AUTH_LOGOUT_CHANNEL = 'aliro-auth-session';
const AUTH_ROUTES = new Set([
    '/',
    '/login',
    '/password-recovery',
    '/register',
    '/reset-password',
    '/set-password',
    '/user-verification',
]);

interface AuthLogoutMessage {
    type: 'logout';
    reason: AuthLogoutReason;
}

const isAuthLogoutReason = (value: unknown): value is AuthLogoutReason =>
    value === 'session-expired' || value === WRONG_PORTAL_LOGOUT_REASON;

const isAuthLogoutMessage = (value: unknown): value is AuthLogoutMessage => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const message = value as Partial<AuthLogoutMessage>;
    return message.type === 'logout' && isAuthLogoutReason(message.reason);
};

export function isAuthenticationRoute(pathname: string): boolean {
    const normalizedPath =
        pathname.length > 1 && pathname.endsWith('/') ? pathname.replace(/\/+$/, '') : pathname;
    return AUTH_ROUTES.has(normalizedPath);
}

export function broadcastAuthLogout(reason: AuthLogoutReason): void {
    if (typeof BroadcastChannel === 'undefined') {
        return;
    }

    try {
        const channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
        channel.postMessage({ type: 'logout', reason } satisfies AuthLogoutMessage);
        channel.close();
    } catch {
        // Cross-tab synchronization is best-effort.
    }
}

export function subscribeToAuthLogout(onLogout: (reason: AuthLogoutReason) => void): () => void {
    if (typeof BroadcastChannel === 'undefined') {
        return () => undefined;
    }

    try {
        const channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
        const handleMessage = (event: MessageEvent<unknown>) => {
            if (isAuthLogoutMessage(event.data)) {
                onLogout(event.data.reason);
            }
        };

        channel.addEventListener('message', handleMessage);
        return () => {
            channel.removeEventListener('message', handleMessage);
            channel.close();
        };
    } catch {
        return () => undefined;
    }
}
